// "PM tie-out" — Setup > Data card (Jordan, 2026-10-20).
//
// The practice management system's aging report, uploaded here, matched claim by claim
// against Atlas's open claims and shown at two bases side by side: `expected` (the
// Waterfall's price less what was collected, Atlas's A/R) and `charge` (billed less
// paid and posted adjustments, what the PM's aging carries). The server does the
// parsing, the match and the bucketing (services/pm_tieout_service.py); this renders
// the payload, posts the files, and downloads a bucket's rows through the shared CSV
// route. Nothing here names a patient: the rows carry Atlas's claim id and the PM's
// claim number, and the parsers never read a name.
//
//   GET  /api/practices/<id>/pm-tie-out                        latest run, history, pm_as_of
//   POST /api/practices/<id>/pm-tie-out                        multipart: insurance, patient
//   GET  /api/practices/<id>/pm-tie-out/runs/<run_id>          one stored run
//   GET  /api/practices/<id>/pm-tie-out/runs/<run_id>/rows.csv ?basis=&bucket=

import React, { useCallback, useEffect, useState } from "react";

import ExportButton from "../reports/ExportButton";
import { formatWhen } from "./DataPage";
import "./PmTieOutCard.css";

const API_URL = process.env.REACT_APP_API_URL || "";

export const BASES = ["expected", "charge"];

export function tieOutUrl(practiceId) {
  return `${API_URL}/api/practices/${practiceId}/pm-tie-out`;
}

export function runUrl(practiceId, runId) {
  return `${tieOutUrl(practiceId)}/runs/${runId}`;
}

// The CSV route under the practice, without ".csv", the way ExportButton takes it.
export function rowsCsvPath(runId) {
  return `pm-tie-out/runs/${runId}/rows`;
}

// Money arrives as text with two decimals ("1234.50", "-12.00"); null is the em dash.
export function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  const abs = Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `-$${abs}` : `$${abs}`;
}

// A difference, signed, so "+$12.00" reads as the PM carrying more and "-$12.00" as
// Atlas carrying more. Zero is "$0.00".
export function formatDiff(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  if (n === 0) return "$0.00";
  return `${n > 0 ? "+" : ""}${formatMoney(value)}`;
}

export function isZero(value) {
  const n = Number(value);
  return value === null || value === undefined || Number.isNaN(n) || n === 0;
}

const MATCH_LABELS = {
  pm_claim_no: "claim #",
  dos_cpt_charge: "DOS + CPTs + charge",
  dos_cpt: "DOS + CPTs",
};

const KIND_LABELS = { insurance: "insurance", patient: "patient" };

async function request(target, options, fallback) {
  const response = await fetch(target, { credentials: "include", ...options });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const err = new Error((body && body.error) || fallback);
    err.status = response.status;
    throw err;
  }
  if (!body || typeof body !== "object") {
    throw new Error("The response could not be read. Try again.");
  }
  return body;
}

function DiffCell({ value }) {
  return (
    <td className={`n${isZero(value) ? " pm-zero" : " pm-diff"}`}>
      {formatDiff(value)}
    </td>
  );
}

// PM | Atlas (expected) | diff | Atlas (charge) | diff, by aging bucket, with the totals.
function AgingTable({ bases }) {
  const expected = bases.expected || {};
  const charge = bases.charge || {};
  const chargeBy = {};
  (charge.by_bucket || []).forEach((row) => {
    chargeBy[row.bucket] = row;
  });
  return (
    <div className="pm-tbl" data-testid="pm-aging">
      <table>
        <thead>
          <tr>
            <th>Aging</th>
            <th className="n">PM</th>
            <th className="n">Atlas (expected)</th>
            <th className="n">Diff</th>
            <th className="n">Atlas (charge)</th>
            <th className="n">Diff</th>
          </tr>
        </thead>
        <tbody>
          {(expected.by_bucket || []).map((row) => {
            const c = chargeBy[row.bucket] || {};
            return (
              <tr key={row.bucket}>
                <td>{row.bucket}</td>
                <td className="n">{formatMoney(row.pm)}</td>
                <td className="n">{formatMoney(row.atlas)}</td>
                <DiffCell value={row.diff} />
                <td className="n">{formatMoney(c.atlas)}</td>
                <DiffCell value={c.diff} />
              </tr>
            );
          })}
          <tr className="pm-total">
            <td>Total</td>
            <td className="n">{formatMoney(expected.pm_total)}</td>
            <td className="n">{formatMoney(expected.atlas_total)}</td>
            <DiffCell value={expected.diff} />
            <td className="n">{formatMoney(charge.atlas_total)}</td>
            <DiffCell value={charge.diff} />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// The buckets at ONE basis (the switch picks which): count, PM $, Atlas $; each row
// opens its rows below.
function BucketTable({ basis, run, showEmpty, openBucket, onOpen }) {
  const side = run.insurance.bases[basis];
  if (!side) return null;
  const labels = run.bucket_labels || {};
  const descriptions = run.bucket_descriptions || {};
  const rows = (side.buckets || []).filter(
    (b) => showEmpty || Number(b.count) > 0,
  );
  return (
    <div className="pm-tbl" data-testid="pm-buckets">
      <table>
        <thead>
          <tr>
            <th>Bucket ({basis})</th>
            <th className="n">Claims</th>
            <th className="n">PM</th>
            <th className="n">Atlas</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr className="pm-muted">
              <td colSpan={4}>Every bucket is empty.</td>
            </tr>
          )}
          {rows.map((b) => (
            <tr
              key={b.key}
              className={`pm-bucket-row${openBucket === b.key ? " pm-open" : ""}${Number(b.count) > 0 ? "" : " pm-muted"}`}
              data-testid={`pm-bucket-${b.key}`}
              title={descriptions[b.key] || ""}
              onClick={() => onOpen(b.key)}
            >
              <td>
                <button
                  type="button"
                  className="data-link-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(b.key);
                  }}
                >
                  {labels[b.key] || b.label || b.key}
                </button>
              </td>
              <td className="n">{b.count}</td>
              <td className="n">{formatMoney(b.pm_dollars)}</td>
              <td className="n">{formatMoney(b.atlas_dollars)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// One bucket's rows, with the CSV of the same (the shared writer: logged, bannered,
// neutralised). The CSV needs a stored run; while storage is pending there is no run id.
function DrillDown({ practiceId, run, basis, bucket, onClose }) {
  const side = run.insurance.bases[basis] || {};
  const rows = (side.rows || {})[bucket] || [];
  const label = (run.bucket_labels || {})[bucket] || bucket;
  const description = (run.bucket_descriptions || {})[bucket];
  const runId = run.run_id;
  return (
    <div className="pm-drill" data-testid="pm-drill">
      <div className="pm-drill-head">
        <div>
          <h3>
            {label} <span className="pm-muted">({basis})</span>
          </h3>
          {description && <p className="data-note">{description}</p>}
        </div>
        <div className="pm-drill-actions">
          {runId ? (
            <ExportButton
              practiceId={practiceId}
              kind="pm-tieout"
              label="Download CSV"
              path={rowsCsvPath(runId)}
              getParams={() => new URLSearchParams({ basis, bucket })}
            />
          ) : (
            <span className="data-note">
              This run was not stored, so there is no CSV.
            </span>
          )}
          <button type="button" className="data-link-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      <div className="pm-tbl">
        <table>
          <thead>
            <tr>
              <th>Claim ID</th>
              <th>PM claim #</th>
              <th>DOS</th>
              <th>CPTs</th>
              <th className="n">Billed</th>
              <th className="n">PM balance</th>
              <th className="n">Atlas open</th>
              <th>Atlas status</th>
              <th>Match</th>
              <th>After sync</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr className="pm-muted">
                <td colSpan={10}>No rows in this bucket.</td>
              </tr>
            )}
            {rows.map((r, i) => (
              <tr key={`${r.claim_id || "-"}-${r.pm_claim_no || "-"}-${i}`}>
                <td>{r.claim_id ?? "—"}</td>
                <td>{r.pm_claim_no || "—"}</td>
                <td>{r.dos || "—"}</td>
                <td>{r.cpts || "—"}</td>
                <td className="n">{formatMoney(r.billed)}</td>
                <td className="n">{formatMoney(r.pm_balance)}</td>
                <td className="n">{formatMoney(r.atlas_open)}</td>
                <td>{r.atlas_status || "—"}</td>
                <td>{r.match ? MATCH_LABELS[r.match] || r.match : "—"}</td>
                <td>{r.after_sync ? "yes" : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// "Patient aging: PM $ vs Atlas $ (of which $ per PM, no ERA)": total to total, the
// patient report carrying no claim lines.
export function patientLine(patient, basis) {
  if (!patient || !patient.present) return "No patient report uploaded.";
  const atlas = (patient.atlas || {})[basis] || {};
  const diff = (patient.diff || {})[basis];
  const perPm =
    basis === "expected" && !isZero(atlas.per_pm)
      ? ` (of which ${formatMoney(atlas.per_pm)} ${atlas.per_pm_label || "per PM, no ERA"})`
      : "";
  return `Patient aging: PM ${formatMoney(patient.pm_total)} vs Atlas ${formatMoney(atlas.total)}${perPm}; diff ${formatDiff(diff)}.`;
}

// The PM's own totals (an AdvancedMD Management Summary uploaded on the AdvancedMD
// card) beside Atlas's, with the delta (PM minus Atlas) on each figure and on each
// aging bucket, insurance and patient. Totals only: no claim and no name.
export function PmTotals({ totals }) {
  if (!totals || !Array.isArray(totals.figures)) return null;
  const aging = totals.aging || {};
  const sides = ["insurance", "patient"].filter((s) => Array.isArray(aging[s]));
  return (
    <div className="pm-totals" data-testid="pm-totals">
      <h3>PM totals (Management Summary)</h3>
      <p className="data-note pm-muted">
        The PM&rsquo;s report as of <strong>{totals.pm && totals.pm.as_of_text ? totals.pm.as_of_text : totals.as_of}</strong>
        {totals.uploaded_at ? <> &middot; uploaded {formatWhen(totals.uploaded_at)}</> : null}
        {totals.period ? <> &middot; Atlas summed {totals.period.from} to {totals.period.to} ({totals.period.claims} claims)</> : null}
        {totals.pm && totals.pm.patients_with_balances ? <> &middot; {totals.pm.patients_with_balances} patients with balances</> : null}
        . Aging at gross charge.
      </p>
      <div className="pm-tbl">
        <table>
          <thead>
            <tr>
              <th>Figure</th>
              <th className="n">PM</th>
              <th className="n">Atlas</th>
              <th className="n">Δ</th>
            </tr>
          </thead>
          <tbody>
            {totals.figures.map((f) => (
              <tr key={f.key} data-testid={`pm-totals-${f.key}`}>
                <td>{f.label}</td>
                <td className="n">{formatMoney(f.pm)}</td>
                <td className="n">{formatMoney(f.atlas)}</td>
                <DiffCell value={f.delta} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sides.map((side) => (
        <div className="pm-tbl" key={side}>
          <table>
            <thead>
              <tr>
                <th>{KIND_LABELS[side]} aging</th>
                <th className="n">PM</th>
                <th className="n">Atlas</th>
                <th className="n">Δ</th>
              </tr>
            </thead>
            <tbody>
              {aging[side].map((r) => (
                <tr key={r.key} className={r.key === "total" ? "pm-total" : ""} data-testid={`pm-totals-${side}-${r.key}`}>
                  <td>{r.label}</td>
                  <td className="n">{formatMoney(r.pm)}</td>
                  <td className="n">{formatMoney(r.atlas)}</td>
                  <DiffCell value={r.delta} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function HistoryTable({ history, currentRunId, onSelect, busy }) {
  if (!history || history.length === 0) return null;
  return (
    <div className="pm-history">
      <h3>Earlier runs</h3>
      <div className="pm-tbl">
        <table>
          <thead>
            <tr>
              <th>Uploaded</th>
              <th>Reports</th>
              <th className="n">Insurance diff (expected)</th>
              <th className="n">Insurance diff (charge)</th>
              <th className="n">Patient diff</th>
            </tr>
          </thead>
          <tbody>
            {history.map((h) => {
              const ins = (h.summary && h.summary.insurance) || {};
              const pat = (h.summary && h.summary.patient) || {};
              const current = h.run_id === currentRunId;
              return (
                <tr
                  key={h.run_id}
                  className={current ? "pm-current" : ""}
                  data-testid={`pm-history-${h.run_id}`}
                >
                  <td>
                    <button
                      type="button"
                      className="data-link-button"
                      disabled={busy || current}
                      onClick={() => onSelect(h.run_id)}
                    >
                      {formatWhen(h.uploaded_at)}
                    </button>
                    {current && <span className="pm-muted"> (shown)</span>}
                  </td>
                  <td>
                    {(h.report_kinds || [])
                      .map((k) => KIND_LABELS[k] || k)
                      .join(", ") || "—"}
                  </td>
                  <DiffCell value={ins.expected ? ins.expected.diff : null} />
                  <DiffCell value={ins.charge ? ins.charge.diff : null} />
                  <DiffCell value={pat.diff ? pat.diff.expected : null} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function PmTieOutCard({ practiceId }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // The route is admins-only while the Data page is not: for any other role the
  // card is not there, rather than a red "Insufficient permissions" on every visit.
  const [hidden, setHidden] = useState(false);
  const [run, setRun] = useState(null);
  const [runError, setRunError] = useState(null);
  const [loadingRun, setLoadingRun] = useState(false);
  const [charge, setCharge] = useState(false);
  const [showEmpty, setShowEmpty] = useState(false);
  const [openBucket, setOpenBucket] = useState(null);
  const [insuranceFile, setInsuranceFile] = useState(null);
  const [patientFile, setPatientFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [message, setMessage] = useState(null);

  const basis = charge ? "charge" : "expected";

  const load = useCallback(() => {
    if (!practiceId) return Promise.resolve(null);
    return request(tieOutUrl(practiceId), {}, "Could not load the PM tie-out.");
  }, [practiceId]);

  useEffect(() => {
    if (!practiceId) return undefined;
    let cancelled = false;
    setData(null);
    setLoadError(null);
    setHidden(false);
    setRun(null);
    setRunError(null);
    setOpenBucket(null);
    setInsuranceFile(null);
    setPatientFile(null);
    setUploadError(null);
    setMessage(null);
    load()
      .then((body) => {
        if (cancelled || !body) return;
        setData(body);
        setRun(body.latest || null);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 403) setHidden(true);
        else setLoadError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId, load]);

  const upload = async () => {
    if (!insuranceFile && !patientFile) return;
    setBusy(true);
    setUploadError(null);
    setMessage(null);
    const body = new FormData();
    if (insuranceFile) body.append("insurance", insuranceFile);
    if (patientFile) body.append("patient", patientFile);
    try {
      const payload = await request(
        tieOutUrl(practiceId),
        { method: "POST", body },
        "The tie-out could not be run.",
      );
      setRun(payload);
      setOpenBucket(null);
      setInsuranceFile(null);
      setPatientFile(null);
      setMessage(
        payload.stored
          ? "Tie-out run and kept."
          : "Tie-out run. It was not stored, so it will be gone on reload.",
      );
      // The history and the pm_as_of stamp come from the server; one re-read rather
      // than a copy of its bookkeeping here.
      load()
        .then((fresh) => {
          if (fresh) setData(fresh);
        })
        .catch(() => {
          /* the run is on screen; the history refreshes on the next visit */
        });
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const selectRun = async (runId) => {
    setLoadingRun(true);
    setRunError(null);
    try {
      const payload = await request(
        runUrl(practiceId, runId),
        {},
        "Could not load that run.",
      );
      setRun(payload);
      setOpenBucket(null);
    } catch (err) {
      setRunError(err.message);
    } finally {
      setLoadingRun(false);
    }
  };

  if (!practiceId || hidden) return null;

  const pmAsOf = run ? run.pm_as_of : data && data.pm_as_of;
  const insurance = run && run.insurance;
  const reports = (run && run.reports) || {};

  return (
    <div className="data-card pm-tieout" data-testid="pm-tieout">
      <h2>PM tie-out</h2>
      <p className="data-note">
        The PM&rsquo;s aging report against Atlas&rsquo;s open claims, matched
        claim by claim and totalled by aging bucket at two bases: expected (the
        price less what was collected, Atlas&rsquo;s A/R) and gross charge
        (billed less paid and posted adjustments, what the PM carries).
      </p>

      {loadError && <p className="data-error-line">{loadError}</p>}

      {data && (
        <>
          <p className="pm-stamps" data-testid="pm-stamps">
            Atlas PM data as of <strong>{formatWhen(pmAsOf)}</strong>
            {run && (
              <>
                {" "}
                &middot; PM report dated{" "}
                <strong>
                  {run.as_of_date
                    ? run.as_of_date
                    : formatWhen(run.uploaded_at)}
                </strong>
              </>
            )}
          </p>
          {data.storage_pending && (
            <p className="data-note pm-muted" data-testid="pm-storage-pending">
              Runs are shown but not kept until migration{" "}
              <code>{data.migration}</code> runs.
            </p>
          )}

          <div className="pm-upload">
            <label className="pm-file">
              <span>Insurance aging (.xls)</span>
              <input
                type="file"
                accept=".xls,.xlsx,application/vnd.ms-excel"
                data-testid="pm-file-insurance"
                disabled={busy}
                onChange={(e) => {
                  const chosen = e.target.files[0];
                  if (chosen) setInsuranceFile(chosen);
                  e.target.value = "";
                }}
              />
              {insuranceFile && (
                <span className="data-filename">{insuranceFile.name}</span>
              )}
            </label>
            <label className="pm-file">
              <span>Patient aging (.xlsx)</span>
              <input
                type="file"
                accept=".xls,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                data-testid="pm-file-patient"
                disabled={busy}
                onChange={(e) => {
                  const chosen = e.target.files[0];
                  if (chosen) setPatientFile(chosen);
                  e.target.value = "";
                }}
              />
              {patientFile && (
                <span className="data-filename">{patientFile.name}</span>
              )}
            </label>
            <button
              type="button"
              className="data-button"
              data-testid="pm-run"
              disabled={busy || (!insuranceFile && !patientFile)}
              onClick={upload}
            >
              {busy ? "Running…" : "Run tie-out"}
            </button>
          </div>
          {data.formats && data.formats.length > 0 && (
            <p className="data-note pm-muted">
              Reads: {data.formats.map((f) => f.label).join("; ")}.
            </p>
          )}

          <PmTotals totals={data.pm_totals} />

          {uploadError && (
            <div
              className="data-message data-message-error"
              data-testid="pm-upload-error"
            >
              <span>{uploadError}</span>
              <button
                type="button"
                className="data-message-close"
                aria-label="Dismiss"
                onClick={() => setUploadError(null)}
              >
                ×
              </button>
            </div>
          )}
          {message && (
            <div className="data-message data-message-success">
              <span>{message}</span>
              <button
                type="button"
                className="data-message-close"
                aria-label="Dismiss"
                onClick={() => setMessage(null)}
              >
                ×
              </button>
            </div>
          )}
          {runError && <p className="data-error-line">{runError}</p>}

          {!run && (
            <p className="data-note" data-testid="pm-empty">
              No tie-out yet. Upload the PM&rsquo;s aging report to compare it
              with Atlas&rsquo;s open claims.
            </p>
          )}

          {run && (
            <>
              <div className="pm-switches">
                <label className={`pm-switch${charge ? " active" : ""}`}>
                  <input
                    type="checkbox"
                    data-testid="pm-basis"
                    checked={charge}
                    onChange={(e) => {
                      // A bucket's rows are one basis's rows: close them.
                      setCharge(e.target.checked);
                      setOpenBucket(null);
                    }}
                  />
                  Show at gross charge
                </label>
                <label className="pm-switch">
                  <input
                    type="checkbox"
                    data-testid="pm-show-empty"
                    checked={showEmpty}
                    onChange={(e) => setShowEmpty(e.target.checked)}
                  />
                  Show empty buckets
                </label>
              </div>

              <dl className="data-facts pm-facts">
                {Object.keys(reports).map((kind) => {
                  const r = reports[kind];
                  return (
                    <React.Fragment key={kind}>
                      <dt>
                        {r.label || `${KIND_LABELS[kind] || kind} report`}
                      </dt>
                      <dd>
                        {r.claims} claims, {r.lines} lines, total{" "}
                        {formatMoney(r.total)}
                        {r.warnings && r.warnings.length > 0 && (
                          <ul className="pm-warnings">
                            {r.warnings.map((w, i) => (
                              <li key={i}>{w}</li>
                            ))}
                          </ul>
                        )}
                      </dd>
                    </React.Fragment>
                  );
                })}
                {run.atlas_window && (
                  <>
                    <dt>Atlas window</dt>
                    <dd>
                      {run.atlas_window.from} to {run.atlas_window.to},{" "}
                      {run.atlas_window.claims} claims
                    </dd>
                  </>
                )}
                {insurance && insurance.present && (
                  <>
                    <dt>Matched</dt>
                    <dd>
                      {insurance.matched} claims
                      {insurance.match_methods &&
                        ` (${Object.keys(insurance.match_methods)
                          .map(
                            (m) =>
                              `${insurance.match_methods[m]} by ${MATCH_LABELS[m] || m}`,
                          )
                          .join(", ")})`}
                    </dd>
                  </>
                )}
              </dl>

              <h3>Insurance aging</h3>
              {insurance && insurance.present ? (
                <>
                  <AgingTable bases={insurance.bases || {}} />
                  {insurance.bases &&
                    insurance.bases[basis] &&
                    insurance.bases[basis].atlas_only_after_sync &&
                    Number(insurance.bases[basis].atlas_only_after_sync.count) >
                      0 && (
                      <p className="data-note pm-muted">
                        {insurance.bases[basis].atlas_only_after_sync.count}{" "}
                        Atlas-only claims (
                        {formatMoney(
                          insurance.bases[basis].atlas_only_after_sync.dollars,
                        )}
                        ) have a date of service after the last PM sync; the
                        next pull brings them.
                      </p>
                    )}
                  <BucketTable
                    basis={basis}
                    run={run}
                    showEmpty={showEmpty}
                    openBucket={openBucket}
                    onOpen={(key) =>
                      setOpenBucket((prev) => (prev === key ? null : key))
                    }
                  />
                  {openBucket && (
                    <DrillDown
                      practiceId={practiceId}
                      run={run}
                      basis={basis}
                      bucket={openBucket}
                      onClose={() => setOpenBucket(null)}
                    />
                  )}
                </>
              ) : (
                <p className="data-note">No insurance report uploaded.</p>
              )}

              <h3>Patient aging</h3>
              <p className="data-note" data-testid="pm-patient">
                {patientLine(run.patient, basis)}
              </p>
            </>
          )}

          <HistoryTable
            history={data.history}
            currentRunId={run ? run.run_id : null}
            onSelect={selectRun}
            busy={loadingRun}
          />
        </>
      )}
    </div>
  );
}
